import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import MosaicCube from "@/components/MosaicCube";
import { MaskedLines, Fade, Overline } from "@/components/Reveal";
import { api } from "@/lib/api";
import { IMAGES } from "@/data/content";

const FALLBACK_BIO =
  "Full biography coming soon.\n\nMiriam brings extensive experience in strategic communications, media relations, stakeholder engagement, multicultural outreach, community relations, political campaigns, project management and event strategy.\n\nA complete professional biography will be added prior to launch.";

const WhoWeAre = () => {
  const [bio, setBio] = useState(FALLBACK_BIO);
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [-30, 30]);

  useEffect(() => {
    api.get("/content/bio").then((res) => setBio(res.data.text)).catch(() => {});
  }, []);

  return (
    <>
      <SEO
        title="Who We Are | Mosaicon Strategies — Toronto Boutique Consultancy"
        description="Mosaicon Strategies is a Greater Toronto Area boutique communications and public affairs consultancy serving businesses, organizations, institutions, community leaders and public figures."
      />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-16 md:pt-28 md:pb-24" data-testid="whoweare-hero">
        <Overline>Who We Are</Overline>
        <h1 className="mt-6 font-serif text-5xl md:text-7xl tracking-tighter text-ink leading-[1.05]" data-testid="whoweare-heading">
          <MaskedLines delay={0.15} lines={["Perspective", <><em className="text-burgundy">matters</em>.</>]} />
        </h1>
      </section>

      {/* Narrative */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 md:pb-32">
        <div className="grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7 space-y-6 text-base md:text-lg leading-relaxed text-ink/75" data-testid="whoweare-narrative">
            <Fade>
              <p className="font-serif text-2xl md:text-3xl leading-snug text-ink tracking-tight">
                Mosaicon Strategies was created around a simple belief: successful communications begin with
                understanding people.
              </p>
            </Fade>
            <Fade delay={0.05}>
              <p>
                Understanding what matters to them. Understanding the communities they belong to. Understanding
                the environment in which decisions are being made. And understanding how relationships, reputation
                and communications intersect.
              </p>
            </Fade>
            <Fade delay={0.1}>
              <p>
                Based in the Greater Toronto Area, Mosaicon Strategies is a boutique communications and public
                affairs consultancy serving businesses, organizations, institutions, community leaders and public
                figures.
              </p>
            </Fade>
            <Fade delay={0.15}>
              <p>
                We combine strategic communications expertise with extensive experience in media, stakeholder
                relations, multicultural community engagement, public affairs, political communications, project
                management and event execution.
              </p>
            </Fade>
            <Fade delay={0.2}>
              <p>
                We approach every mandate individually. Before recommending tactics, we listen. We identify the
                objective, understand the audiences and stakeholders involved, assess opportunities and risks and
                build a strategy designed around the client&apos;s specific circumstances.
              </p>
            </Fade>
            <Fade delay={0.25}>
              <p>
                Whether the objective is introducing an organization to a new community, managing a difficult
                issue, strengthening media visibility, engaging government, building stakeholder support or
                running a political campaign, our focus remains the same:
              </p>
            </Fade>
            <Fade delay={0.3}>
              <p className="font-serif text-xl md:text-2xl text-burgundy tracking-tight border-l-2 border-gold pl-6">
                Clear strategy, credible communications and meaningful relationships.
              </p>
            </Fade>
          </div>
          <div className="lg:col-span-5" ref={imgRef}>
            <Fade delay={0.15} className="relative lg:sticky lg:top-32">
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-teal" aria-hidden="true" />
              <div className="overflow-hidden">
                <motion.img
                  src={IMAGES.heroToronto}
                  alt="Abstract view of Toronto architecture — the complex, diverse metropolitan market Mosaicon calls home"
                  className="w-full aspect-[4/5] object-cover scale-110"
                  style={{ y: imgY }}
                  loading="lazy"
                  data-testid="whoweare-image"
                />
              </div>
              <p className="mt-4 text-xs uppercase tracking-[0.2em] text-ink/40">
                Based in the Greater Toronto Area
              </p>
            </Fade>
          </div>
        </div>
      </section>

      {/* Boutique by design */}
      <section className="bg-white border-y border-ink/10" data-testid="boutique-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-4 flex justify-center order-last lg:order-first">
            <MosaicCube className="w-56 md:w-72 h-auto" />
          </div>
          <div className="lg:col-span-8">
            <Overline>Boutique by Design</Overline>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl tracking-tight text-ink" data-testid="boutique-heading">
              <MaskedLines immediate={false} lines={["Individual pieces. Different", "perspectives. One stronger picture."]} />
            </h2>
            <Fade delay={0.15} className="mt-8 space-y-6 text-base leading-relaxed text-ink/70 max-w-2xl">
              <p>Being boutique allows us to stay close to our clients.</p>
              <p>
                Every engagement receives senior-level attention, responsive counsel and a customized approach.
                When projects require additional capabilities, Mosaicon can assemble trusted specialists and
                community partners appropriate to the assignment.
              </p>
              <p>
                We believe the strongest solutions often come from bringing together different perspectives,
                disciplines and communities.
              </p>
              <p>
                That philosophy is reflected in our name: <span className="font-serif text-burgundy text-lg">Mosaicon.</span>
              </p>
            </Fade>
          </div>
        </div>
      </section>

      {/* Principal Consultant */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32" data-testid="principal-section">
        <Overline>Founding Principal Consultant</Overline>
        <div className="mt-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-4">
            <Fade>
              <div
                className="aspect-[3/4] bg-sand border border-ink/10 relative overflow-hidden"
                data-testid="principal-portrait"
              >
                <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-burgundy/40 z-10" aria-hidden="true" />
                <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-burgundy/40 z-10" aria-hidden="true" />
                <img
                  src="/assets/miriam-ku.jpeg"
                  alt="Miriam Ku, Principal Consultant of Mosaicon Strategies"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </Fade>
          </div>
          <div className="lg:col-span-8">
            <Fade delay={0.1}>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-ink" data-testid="principal-name">
                Miriam Ku
              </h2>
              <p className="mt-3 text-xs uppercase tracking-[0.28em] text-burgundy font-semibold">
                Founding Principal Consultant
              </p>
            </Fade>
            <Fade delay={0.2} className="mt-8 space-y-5 max-w-2xl" data-testid="principal-bio">
              {bio.split(/\n{2,}/).map((para, i) => (
                <p key={i} className={`leading-relaxed ${i === 0 ? "font-serif text-xl text-ink" : "text-base text-ink/70"}`}>
                  {para}
                </p>
              ))}
            </Fade>
            <Fade delay={0.3} className="mt-10">
              <Link
                to="/contact"
                data-testid="principal-contact-btn"
                className="group inline-flex items-center gap-3 text-sm uppercase tracking-[0.18em] font-semibold text-burgundy"
              >
                <span className="link-underline">Speak with Miriam</span>
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Fade>
          </div>
        </div>
      </section>
    </>
  );
};

export default WhoWeAre;
